import {
  type DistanceUnit as CoreDistanceUnit,
  convert,
  type FormatBundle,
  labelFor,
  type PressureUnit,
} from '@brightdrop/localization-core';

import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import { readMountedFormatBundle } from '~/common/localization/diagnosticFormat';
import {
  type AssetDiagnostics,
  type AssetFlags,
  AssetIgnitionStatus,
  AssetOperationalStatus,
  type AssetTelemetry,
  CapacityUnit,
  DiagnosticSeverity,
  EnergyEfficiency,
  FuelEconomyUnit,
  SpeedUnit,
  type TelemetryStatus,
} from '~/common/models/asset.model';
import { isUnsafeObjectKey } from '~/common/utils/common.utils';
import { isAfter } from '~/common/utils/date-time.utils';

import {
  RemoteCommand,
  RemoteCommandPowerIgnitionStatus,
} from '~/routes/organizations/vehicles/details/components/remoteCommands/remoteCommands.constants';

// Accepts alphanumeric characters, hyphens, and underscores.
// This covers VINs (uppercase alphanumeric), UUIDs (alphanumeric + hyphens),
// and internal platform identifiers (alphanumeric + underscores).
// Dots are intentionally excluded to prevent property-path traversal patterns
// (e.g. "param.name") if the ID is used as a key in nested-access utilities.
// Note: createSafeSessionId (session IDs) allows dots; asset IDs are stricter.
// Reserved-name rejection delegates to isUnsafeObjectKey (single source of truth).
const ASSET_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

/**
 * Returns true if the given asset ID string is safe to use as an API parameter
 * or selector key. An empty string is considered valid (absent, not malformed).
 */
export const isAssetIdValid = (id: string): boolean => {
  if (id === '') return true;
  return ASSET_ID_PATTERN.test(id) && !isUnsafeObjectKey(id);
};

export const ENERGY_EFFICIENCY_CONVERSIONS = {
  [EnergyEfficiency.KM]: {
    [EnergyEfficiency.MI]: 1.60934,
  },
  [EnergyEfficiency.MI]: {
    [EnergyEfficiency.KM]: 0.621371,
  },
};

export const CAPACITY_CONVERSIONS = {
  [CapacityUnit.GALLON]: {
    [CapacityUnit.LITER]: 3.79,
  },
};

export const FUEL_ECONOMY_CONVERSIONS = {
  [FuelEconomyUnit.MPG]: {
    [FuelEconomyUnit.L100K]: 235.215,
  },
  [FuelEconomyUnit.L100K]: {
    [FuelEconomyUnit.MPG]: 235.215,
  },
};

export const SPEED_CONVERSION = {
  [SpeedUnit.MPH]: {
    [SpeedUnit.MPS]: 0.44704,
    [SpeedUnit.KPH]: 1.60934,
  },
};

export const convertMeasurement = <T extends string>(
  value: number,
  conversions: { [key in T]?: { [key in T]?: number } },
  fromUnit: T,
  toUnit: T,
  conversionFromUnitFormula: (value: number, conversion: number) => number = (
    value: number,
    conversion: number
  ) => value * conversion,
  conversionToUnitFormula: (value: number, conversion: number) => number = (
    value: number,
    conversion: number
  ) => value / conversion
): number => {
  if (fromUnit === toUnit) {
    return value;
  }
  const conversionsFromUnit = conversions[fromUnit];
  if (conversionsFromUnit) {
    const conversion = conversionsFromUnit[toUnit];
    if (conversion) {
      return conversionFromUnitFormula(value, Number(conversion));
    }
  }
  const conversionsToUnit = conversions[toUnit];
  if (conversionsToUnit) {
    const conversion = conversionsToUnit[fromUnit];
    if (conversion) {
      return conversionToUnitFormula(value, Number(conversion));
    }
  }
  return Number.NaN;
};

/**
 * An electric consumption figure in another unit.
 *
 * @deprecated Use `<Efficiency>` from `@brightdrop/localization-core`. Not a mechanical swap:
 * `EnergyEfficiency.KM` (`kWh/km`) meets the core's per-hundred metric unit, and
 * `EnergyEfficiency.MI` (`kWh/mi`) meets `MI_PER_KWH`, which runs the other way, so that move is
 * a reciprocal as well as a rescaling.
 */
export const convertEnergyEfficiency = (
  value: number,
  fromUnit: EnergyEfficiency,
  toUnit: EnergyEfficiency
): number => {
  return convertMeasurement(
    value,
    ENERGY_EFFICIENCY_CONVERSIONS,
    fromUnit,
    toUnit
  );
};

/**
 * A speed in another unit.
 *
 * @deprecated Use `<Speed>` from `@brightdrop/localization-core`, or
 * `useFormat().speed.raw(value, { valueUnit })`. `valueUnit` is the unit the backend sent.
 */
export const convertSpeed = (
  value: number,
  fromUnit: SpeedUnit,
  toUnit: SpeedUnit
): number => {
  return convertMeasurement(value, SPEED_CONVERSION, fromUnit, toUnit);
};

/**
 * A volume in another unit.
 *
 * @deprecated Use `<Volume>` from `@brightdrop/localization-core`, or
 * `useFormat().capacity.raw(value, { valueUnit })`. `valueUnit` is the unit the backend sent.
 * The core distinguishes the US gallon from the imperial one; `CapacityUnit.GALLON` says neither
 * which gallon it means nor how many litres it is.
 */
export const convertCapacity = (
  value: number,
  fromUnit: CapacityUnit,
  toUnit: CapacityUnit
): number => {
  return convertMeasurement(value, CAPACITY_CONVERSIONS, fromUnit, toUnit);
};

export const convertKmpLToL100km = (kilometersPerLiter: number) => {
  return kilometersPerLiter > 0 ? 100 / kilometersPerLiter : 0;
};

/**
 * A fuel economy figure in another unit.
 *
 * @deprecated Use `<FuelEconomy>` from `@brightdrop/localization-core`, or
 * `useFormat().fuelConsumption.raw(value, { valueUnit })`. `valueUnit` is the unit the backend
 * sent, named in the core's vocabulary rather than this one: `mpg` is `MPG_US`, since the core
 * tells the US gallon from the imperial `MPG_UK`, and `L/100 km` is `L_PER_100KM`. Nothing there
 * stands for `km/L`, so a reading in it has to go through `convertKmpLToL100km` above before the
 * core sees it. Mind the guard when moving a call site: `0` here means "no reading", and because
 * both directions of this conversion are reciprocal, passing it through would divide by it.
 */
export const convertFuelEconomy = (
  value: number,
  fromUnit: FuelEconomyUnit,
  toUnit: FuelEconomyUnit
): number => {
  return value > 0
    ? convertMeasurement(
        value,
        FUEL_ECONOMY_CONVERSIONS,
        fromUnit,
        toUnit,
        (value: number, conversion: number) => conversion / value,
        (value: number, conversion: number) => conversion / value
      )
    : 0;
};

/**
 * A tyre pressure in another unit, rounded to whole units.
 *
 * @deprecated Use `<Pressure>` from `@brightdrop/localization-core`, which takes its digit count
 * from the precision policy rather than always rounding to an integer. That makes the move a
 * visible change wherever a caller relies on the whole number this promises.
 */
export const convertTirePressure = (
  value: number,
  fromUnit: PressureUnit,
  toUnit: PressureUnit
): number => {
  if (fromUnit === toUnit) {
    return Math.round(value);
  }
  return Math.round(convert('pressure', value, fromUnit, toUnit));
};

export const getMaxDiagnosticFlagDate = (
  flags?: AssetDiagnostics
): Date | undefined => {
  const maxDate =
    !!flags &&
    Object.keys(flags || []).reduce((maxDate, key) => {
      const flag = flags[key as keyof typeof flags];
      if (flag) {
        const timestamp =
          flag.deviceTimestamp !== undefined
            ? new Date(flag.deviceTimestamp).getTime()
            : 0;
        return !maxDate
          ? timestamp
          : !timestamp
            ? maxDate
            : Math.max(maxDate, timestamp);
      }
      return maxDate;
    }, 0);
  return maxDate ? new Date(maxDate) : undefined;
};

export const getMaxTelemetryDate = (
  status?: AssetTelemetry
): Date | undefined => {
  const filteredDates = [
    status?.location?.deviceTimestamp,
    status?.batteryStatus?.battery?.deviceTimestamp,
    status?.batteryStatus?.charge?.deviceTimestamp,
    status?.chargingStatus?.deviceTimestamp,
    status?.diagnostics?.deviceTimestamp, // c_diag
    status?.lock?.deviceTimestamp,
    status?.door?.deviceTimestamp,
    status?.odometer?.deviceTimestamp,
    status?.estimatedRange?.deviceTimestamp,
    status?.speedStatus?.deviceTimestamp,
    status?.direction?.deviceTimestamp,
    status?.tirePressures?.leftFront?.deviceTimestamp,
    status?.tirePressures?.leftRear?.deviceTimestamp,
    status?.tirePressures?.rightFront?.deviceTimestamp,
    status?.tirePressures?.rightRear?.deviceTimestamp,
  ]
    .map((date) => (date ? new Date(date).getTime() : 0))
    .filter((date) => !!date);

  return filteredDates.length
    ? new Date(Math.max(...filteredDates))
    : undefined;
};

export const getOperationalStatusDurationDate = ({
  lastUpdated,
  operationalStatus,
  telemetry,
  flags,
}: {
  lastUpdated?: string;
  operationalStatus?: AssetOperationalStatus;
  telemetry?: AssetTelemetry;
  flags?: AssetFlags;
}): Date | undefined => {
  let lastStatusUpdate: Date | undefined;
  switch (operationalStatus) {
    case AssetOperationalStatus.OFFLINE: {
      const lastTelemetryUpdate = getMaxTelemetryDate(telemetry);
      const lastDiagnosticUpdate = getMaxDiagnosticFlagDate(flags?.diagnostics);
      lastStatusUpdate = lastUpdated
        ? new Date(lastUpdated)
        : lastTelemetryUpdate && lastDiagnosticUpdate
          ? new Date(
              Math.max(
                lastTelemetryUpdate.getTime(),
                lastDiagnosticUpdate.getTime()
              )
            )
          : lastTelemetryUpdate || lastDiagnosticUpdate;
      break;
    }
    case AssetOperationalStatus.MOVING: {
      const speedTimestamp = telemetry?.speedStatus?.deviceTimestamp;
      const directionTimestamp = telemetry?.direction?.deviceTimestamp;
      if (speedTimestamp && directionTimestamp) {
        const lastSpeedUpdate = new Date(speedTimestamp);
        const lastDirectionUpdate = new Date(directionTimestamp);
        lastStatusUpdate = isAfter(lastSpeedUpdate, lastDirectionUpdate)
          ? lastSpeedUpdate
          : lastDirectionUpdate;
        break;
      }
      lastStatusUpdate = speedTimestamp
        ? new Date(speedTimestamp)
        : directionTimestamp
          ? new Date(directionTimestamp)
          : undefined;
      break;
    }
    case AssetOperationalStatus.MOVING_NO_DIRECTION:
    case AssetOperationalStatus.STOPPED:
      lastStatusUpdate = telemetry?.speedStatus?.deviceTimestamp
        ? new Date(telemetry?.speedStatus?.deviceTimestamp)
        : undefined;
      break;
  }
  return lastStatusUpdate;
};

export const formatLabelWithUom = (
  value: string | number | undefined | null,
  uom: string | undefined
) => (value != null ? `${value} ${uom}` : '');

export const convertToLocalizedDecimalString = (value: number) => {
  return Number.isInteger(value)
    ? value.toLocaleString()
    : Number(value.toFixed(1)).toLocaleString();
};

/**
 * The severity and rounded percent label for a 0–100 telemetry reading.
 * @param telemetryStatus - The reading, such as oil life or brake pad wear.
 * @param formatPercent - Formats the rounded percent; omitted, it reads `85%`.
 */
export const getPercentageStatusAndLabel = (
  telemetryStatus?: TelemetryStatus<number>,
  formatPercent?: (roundedPercent: number) => string
) => {
  if (!telemetryStatus) return null;

  const value = telemetryStatus?.value;
  const severity = telemetryStatus?.severity || DiagnosticSeverity.UNKNOWN;
  const badgeLabel =
    value != null
      ? formatPercent
        ? formatPercent(Math.round(value))
        : Math.round(value) + '%'
      : DEFAULT_EMPTY_VALUE;

  return {
    severity: severity,
    label: badgeLabel,
  };
};

const formatLegacyDistanceAmount = (
  converted: number,
  roundValue: boolean,
  useCommaFormat: boolean
): string => {
  if (!useCommaFormat) {
    return roundValue ? Math.round(converted).toString() : converted.toString();
  }
  const format = readMountedFormatBundle();
  if (format) {
    return roundValue
      ? format.number(Math.round(converted), { digits: 0 })
      : format.number(converted);
  }
  const amount = roundValue ? Math.round(converted) : converted;
  return convertToLocalizedDecimalString(amount);
};

/**
 * Lifetime EV efficiency arrives as miles per kWh; the range card shows kWh/mi (or metric consumption).
 */
export const formatLifetimeEvEfficiencyFromMilesPerKwh = (
  milesPerKwh: number,
  format: FormatBundle
): string => {
  if (format.electricConsumption.unit === 'KWH_PER_100KM') {
    return format.electricConsumption.format(milesPerKwh, {
      valueUnit: 'MI_PER_KWH',
    });
  }

  const kwhPerMi = 1 / milesPerKwh;
  return `${format.number(kwhPerMi, { digits: 2 })} kWh/mi`;
};

export const formatDistance = ({
  units,
  value,
  locale = 'en-US',
  showUnit = true,
  emptyValue,
  roundValue = true,
  useCommaFormat = true,
}: {
  units: CoreDistanceUnit;
  value?: number;
  locale?: string;
  showUnit?: boolean;
  emptyValue?: string;
  roundValue?: boolean;
  useCommaFormat?: boolean;
}): string => {
  const snapshot = readMountedFormatBundle();

  return typeof value === 'number'
    ? value === 0 && emptyValue
      ? emptyValue
      : (() => {
          const converted = snapshot
            ? snapshot.distance.raw(value, { valueUnit: 'KM' })
            : convert('distance', value, 'KM', units);
          const amount = formatLegacyDistanceAmount(
            converted,
            roundValue,
            useCommaFormat
          );
          const unitSuffix = snapshot
            ? snapshot.distance.label()
            : labelFor(locale, 'distance', units);
          return showUnit ? `${amount} ${unitSuffix}` : amount;
        })()
    : DEFAULT_EMPTY_VALUE;
};

/**
 * Maps the current ignition and power status to the appropriate remote ignition command
 * and determines if the command is allowed to be executed.
 *
 * @param powerStatus - The current telemetry status of the remote command power ignition, or null/undefined.
 * @param ignitionStatus - The current ignition status of the asset.
 * @param currentCommand - The current remote command, if any.
 * @returns An object containing:
 *   - `command`: The remote ignition command to be executed, or undefined.
 *   - `isAllowed`: A boolean indicating whether the command is allowed to be executed.
 */
export const mapIgnitionCommand = (
  powerStatus?: TelemetryStatus<RemoteCommandPowerIgnitionStatus> | null,
  ignitionStatus?: AssetIgnitionStatus,
  currentCommand?: RemoteCommand
): {
  command: RemoteCommand | undefined;
  isAllowed: boolean;
} => {
  const isPowerOff =
    powerStatus?.value === RemoteCommandPowerIgnitionStatus.OFF;
  const isPowerOn = powerStatus?.value === RemoteCommandPowerIgnitionStatus.ON;
  const isIgnitionEnabled = ignitionStatus === AssetIgnitionStatus.ENABLED;
  const isIgnitionDisabled = ignitionStatus === AssetIgnitionStatus.DISABLED;
  const isIgnitionUnknown = ignitionStatus === AssetIgnitionStatus.UNKNOWN;

  if (!powerStatus?.value) return { command: currentCommand, isAllowed: false };

  if (isPowerOff && (isIgnitionEnabled || isIgnitionUnknown)) {
    return { command: RemoteCommand.IGNITION_DISABLE, isAllowed: true };
  }

  if (isPowerOn) {
    return {
      command: isIgnitionDisabled
        ? RemoteCommand.IGNITION_ENABLE
        : RemoteCommand.IGNITION_DISABLE,
      isAllowed: false,
    };
  }

  return { command: RemoteCommand.IGNITION_ENABLE, isAllowed: true };
};
